import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import Pagination from "@mui/material/Pagination";

import ClearIcon from "@mui/icons-material/Close";

import { get } from "lib/api";
import MobileTopBar from "mobile/components/MobileTopBar";
import { IosList, IosListItem, IosPage } from "mobile/components/IosParity";

const normalize = (d) =>
  d?.attributes ? { id: d.id, documentId: d.documentId || d.id, ...d.attributes } : { ...d, documentId: d.documentId || d.id };

const formatDate = (v) => (v ? String(v).slice(0, 10) : "—");

const formatMoney = (value) => {
  const n = value == null ? 0 : Number(value);
  if (!Number.isFinite(n)) return "$0.00";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
};

export default function PaymentsMobile() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 20;

  useEffect(() => setPage(1), [search]);

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ["m-payments", search, page, pageSize],
    queryFn: () =>
      get("/api/payments", {
        sort: "paymentDate:desc",
        pagination: { page, pageSize },
        populate: { invoice: true, purchaseOrder: true },
        filters: {
          ...(search && {
            $or: [{ paymentNumber: { $containsi: search } }, { referenceNumber: { $containsi: search } }],
          }),
        },
      }),
    staleTime: 10_000,
  });

  const rows = useMemo(() => (data?.data || []).map(normalize), [data]);
  const pageCount = data?.meta?.pagination?.pageCount || 1;
  const total = data?.meta?.pagination?.total;

  return (
    <IosPage>
      <MobileTopBar
        title="Payments"
        subtitle={typeof total === "number" ? `${total} total • page ${page}/${pageCount}` : isFetching ? "Syncing…" : "List"}
        onBack={() => navigate("/m/dashboard")}
      />

      <Box sx={{ mt: 1, mb: 1 }}>
        <TextField
          fullWidth
          placeholder="Search payments…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon fontSize="small">search</Icon>
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" aria-label="Clear search" onClick={() => setSearch("")}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      </Box>

      {isLoading ? <LinearProgress sx={{ mb: 1 }} /> : null}
      {error ? (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          Failed to load payments.
        </Typography>
      ) : null}

      <IosList>
        {rows.map((p) => {
          const documentId = p.documentId;
          const invoiceRaw = p?.invoice?.data || p?.invoice || null;
          const invoice = invoiceRaw ? normalize(invoiceRaw) : null;
          const poRaw = p?.purchaseOrder?.data || p?.purchaseOrder || null;
          const po = poRaw ? normalize(poRaw) : null;
          const amount = p.amountInUSD != null ? p.amountInUSD : p.amount;
          const relLabel = invoice?.salesOrderNumber || po?.purchaseOrderNumber || "";
          return (
            <IosListItem
              key={documentId || p.id}
              icon="payments"
              title={p.paymentNumber || "Payment"}
              meta={`${formatDate(p.paymentDate)} • ${p.paymentMethod === "credit_memo" ? "Credit Memo" : p.satPaymentForm || "—"}${
                relLabel ? ` • ${relLabel}` : ""
              }`}
              trailing={formatMoney(amount)}
              onClick={() => {
                if (documentId) navigate(`/m/payments/${documentId}`);
              }}
            />
          );
        })}
      </IosList>

      {pageCount > 1 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <Pagination size="small" count={pageCount} page={page} onChange={(e, p) => setPage(p)} />
        </Box>
      ) : null}
    </IosPage>
  );
}
